import { AmountSuperScript } from "components/design/AmountSuperScript";
import { FileIcon } from "components/design/FileIcon";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import AutopayIcon from "static/images/Autopay.svg";
import BankTransfer from "static/images/BankTransfer.svg";
import { BillingInvoice } from "types/Models/billing";
import { formatDate } from "utils/formatDate";
import { Preview } from "components/PreviewModal";

const columnHelper = createColumnHelper<BillingInvoice>();

export const InvoiceTopayModal = ({
  show,
  closeModal,
  unPaidInvoice,
  PayUnpaid,
  totalAmount,
}: {
  show: boolean;
  closeModal: () => void;
  unPaidInvoice: BillingInvoice[];
  PayUnpaid: () => void;
  totalAmount: number;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: "Invoice Name",
        size: 20,
        cell: (info) => {
          const {
            late_fee,
            due_date,
            is_auto_pay_enabled,
            is_in_bank_transfer,
          } = info.row.original;

          return (
            <div className="t-flex t-items-center t-gap-2">
              <div>{info.getValue()}</div>
              {due_date && is_auto_pay_enabled && (
                <ToolTip text={`Due on ${formatDate(due_date)}`}>
                  <img src={AutopayIcon} alt="AutopayIcon" />
                </ToolTip>
              )}
              {Number(late_fee) > 0 && (
                <Tag tagType="red" icon={false} rounded={true}>
                  Overdue
                </Tag>
              )}
              {is_in_bank_transfer && (
                <ToolTip text="The bank transfer is in progress for this invoice.">
                  <img src={BankTransfer} alt="BankTransfer" />
                </ToolTip>
              )}
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("created_at", {
        header: "Date of Creation",
        size: 14,
        cell: (info) => formatDate(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor("document.uuid", {
        header: "Invoice",
        size: 8,
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <span className="invoiceViewDownload">
              {rowData?.document ? (
                <span
                  className="invoiceIcon"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewFileId(rowData?.document?.uuid!);
                  }}
                >
                  <FileIcon fileType={rowData?.document?.file_type} size="30" />
                </span>
              ) : (
                <>-</>
              )}
            </span>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("amount", {
        header: "Amount",
        size: 12,
        cell: (info) => {
          const { amount, discount } = info.row.original;
          return (
            <div className="t-flex t-justify-end">
              <AmountSuperScript
                amount={
                  discount ? Number(amount) - Number(discount) : Number(amount)
                }
              />
            </div>
          );
        },
        enableSorting: false,
      }),
    ];
  }, []);

  const table = useReactTable({
    data: unPaidInvoice,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <>
      <Modal.Root open={show} onOpenChange={closeModal}>
        <Modal.Content className="t-w-[680px]">
          <Modal.Header>
            <Modal.Title>Pay Outstanding Invoices</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-flex t-flex-col t-gap-3 t-h-[330px] t-overflow-auto t-mb-4">
              <TableUI
                table={table}
                header={({ headerGroup }) => (
                  <Table.HeadRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.HeadCell
                        key={header.id}
                        style={{ width: `${header.getSize()}%` }}
                      >
                        <span
                          className={`t-flex t-gap-1 t-items-center ${
                            header.column.id === "amount"
                              ? "t-justify-end"
                              : "t-justify-start"
                          }`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                      </Table.HeadCell>
                    ))}
                  </Table.HeadRow>
                )}
              >
                {({ row }) => (
                  <Table.Row
                    key={row.original.uuid}
                    className="hover:t-bg-surface-lighter-grey"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{
                          width: `${cell.column.getSize()}%`,
                          padding: "12px 8px",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                )}
              </TableUI>
            </div>
            <div className="t-flex t-justify-end t-items-center t-gap-10 t-font-bold">
              <span className="t-text-text-30 t-font-normal t-text-subtext-sm">
                TOTAL :
              </span>
              <AmountSuperScript amount={totalAmount} />
            </div>
          </Modal.Body>
          <Modal.Footer className="t-flex t-w-full t-items-center t-gap-3 t-justify-end">
            <Button type="button" onClick={closeModal}>
              Go Back
            </Button>
            <Button customType="primary" onClick={PayUnpaid}>
              <div className="t-flex t-gap-1">
                <span>Pay</span> <AmountSuperScript amount={totalAmount} />
              </div>
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};
